import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Split, Flame, Filter, Megaphone, Video,
  Check, MousePointer2, X, Sparkles, Pause, SkipForward, ShoppingCart, Users
} from 'lucide-react';

const TAB_ORDER = ['ab', 'heatmap', 'funnel', 'replay', 'personalization', 'popup'];
const ROLL_INTERVAL = 6000;

const FeatureShowcase = () => {
  const [active, setActive] = useState('ab');
  const [paused, setPaused] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true); // 유저가 탭 클릭 시 영구 정지

  const prefersReduced = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // 자동 롤링 (hover/focus 시 일시정지, 탭 클릭 시 영구 정지, 모션 최소화 설정 시 비활성)
  useEffect(() => {
    if (prefersReduced || !autoPlay || paused) return;

    const interval = setInterval(() => {
      setActive((prev) => TAB_ORDER[(TAB_ORDER.indexOf(prev) + 1) % TAB_ORDER.length]);
    }, ROLL_INTERVAL);
    return () => clearInterval(interval);
  }, [prefersReduced, autoPlay, paused]);

  const handleTabClick = (id) => {
    setActive(id);
    setAutoPlay(false);
  };

  const features = [
    {
      id: 'ab',
      name: 'A/B 테스트',
      role: '실험',
      icon: <Split className="w-4 h-4" />,
      title: '방문자 절반에게는 A안을, 절반에게는 B안을 보여줍니다',
      desc: '어느 쪽이 더 팔리는지, 감이 아니라 숫자로 확인하세요. 에디터에서 바꾸고 실험을 켜는 데 5분이면 충분합니다.',
      bullets: ['개발 배포 없이 문구·디자인 즉시 변경', 'URL 분할로 페이지 전체 비교', '감이 아닌 통계로 승자 판정'],
      link: '/service/ab-test',
      tabActive: 'bg-blue-600 text-white shadow-md shadow-blue-500/25',
      accent: 'text-blue-400',
      accentHover: 'hover:text-blue-300',
      check: 'text-blue-400',
      bar: 'bg-blue-500',
      ring: 'focus-visible:ring-blue-500'
    },
    {
      id: 'heatmap',
      name: '히트맵',
      role: '관찰',
      icon: <Flame className="w-4 h-4" />,
      title: '이탈률 68%. GA는 여기까지만 말해줍니다',
      desc: '그 68%가 어디를 클릭하다 포기했는지, 어디서 스크롤을 멈췄는지는 히트맵이 보여줍니다. 아무도 안 누르는 버튼, 버튼인 줄 알고 누르는 이미지까지.',
      bullets: ['클릭맵 · 스크롤맵 · 체류시간맵', '분노 클릭 · 아무도 안 보는 영역 발견', '모바일과 데스크톱의 다른 행동 구분'],
      link: '/service/heatmap',
      tabActive: 'bg-orange-500 text-white shadow-md shadow-orange-500/25',
      accent: 'text-orange-400',
      accentHover: 'hover:text-orange-300',
      check: 'text-orange-400',
      bar: 'bg-orange-500',
      ring: 'focus-visible:ring-orange-500'
    },
    {
      id: 'funnel',
      name: '퍼널',
      role: '분석',
      icon: <Filter className="w-4 h-4" />,
      title: '10,000명이 들어와서 310명만 결제하는 이유',
      desc: '장바구니에서 잃었는지, 결제창에서 잃었는지에 따라 해야 할 일이 완전히 다릅니다. 단계마다 몇 명이 남고 몇 명이 떠났는지 정확히 세어드립니다.',
      bullets: ['어느 단계에서 몇 명이 떠나는지 추적', '구매 결정까지 걸리는 시간 측정', '새로고침에도 끊기지 않는 정밀 추적'],
      link: '/service/funnel',
      tabActive: 'bg-purple-600 text-white shadow-md shadow-purple-500/25',
      accent: 'text-purple-400',
      accentHover: 'hover:text-purple-300',
      check: 'text-purple-400',
      bar: 'bg-purple-500',
      ring: 'focus-visible:ring-purple-500'
    },
    {
      id: 'replay',
      name: '세션 녹화',
      role: '재현',
      icon: <Video className="w-4 h-4" />,
      title: '이탈한 고객 한 명의 화면을, 그 사람이 본 그대로',
      desc: '히트맵이 수천 명의 경향이라면 세션 녹화는 한 명의 전말입니다. 어디서 헤매고 무엇을 누르다 떠났는지 직접 재생해서 확인하세요. 입력값은 전부 가려진 채로만 저장됩니다.',
      bullets: ['마우스 · 클릭 · 스크롤 · 입력을 그대로 재생', '클릭 · 페이지 이동 타임라인으로 장면 점프', '입력값 마스킹 기본 적용 · 해제 불가'],
      link: '/service/replay',
      tabActive: 'bg-cyan-600 text-white shadow-md shadow-cyan-500/25',
      accent: 'text-cyan-400',
      accentHover: 'hover:text-cyan-300',
      check: 'text-cyan-400',
      bar: 'bg-cyan-500',
      ring: 'focus-visible:ring-cyan-500'
    },
    {
      id: 'personalization',
      name: '개인화',
      role: '맞춤',
      icon: <Users className="w-4 h-4" />,
      title: '같은 페이지, 고객마다 다른 내용',
      desc: '처음 온 고객에게는 가입 혜택을, 장바구니에 담고 떠난 고객에게는 할인을, 단골에게는 신상품 소식을. 고객에게 맞는 내용을 보여주고, 기존 화면과 비교해 효과까지 확인합니다.',
      bullets: ['회원 여부 · 장바구니 · 등급별 타게팅', '비주얼 에디터로 고객별 내용 수정', '비교 그룹으로 개인화의 진짜 효과 측정'],
      link: '/service/personalization',
      tabActive: 'bg-pink-600 text-white shadow-md shadow-pink-500/25',
      accent: 'text-pink-400',
      accentHover: 'hover:text-pink-300',
      check: 'text-pink-400',
      bar: 'bg-pink-500',
      ring: 'focus-visible:ring-pink-500'
    },
    {
      id: 'popup',
      name: '팝업/배너',
      role: '실행',
      icon: <Megaphone className="w-4 h-4" />,
      title: '나가려는 고객, 3초 안에 붙잡기',
      desc: '이탈 직전 고객에게 쿠폰을, 재방문 고객에게는 다른 메시지를. 홀드아웃 그룹으로 "팝업이 없었어도 샀을 사람"까지 걸러내 진짜 효과만 측정합니다.',
      bullets: ['모달 · 띠배너 · 슬라이드 · 전면 팝업', '신규 · 재방문 고객에게 다른 메시지', '홀드아웃으로 팝업의 진짜 효과 검증'],
      link: '/service/popup',
      tabActive: 'bg-emerald-600 text-white shadow-md shadow-emerald-500/25',
      accent: 'text-emerald-400',
      accentHover: 'hover:text-emerald-300',
      check: 'text-emerald-400',
      bar: 'bg-emerald-500',
      ring: 'focus-visible:ring-emerald-500'
    }
  ];

  const activeF = features.find((f) => f.id === active) || features[0];

  const renderMockup = (id) => {
    switch (id) {
      /* ===== A/B 테스트: 비주얼 에디터 ===== */
      case 'ab':
        return (
          <div className="bg-white rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10 overflow-hidden">
            {/* 에디터 상단 바 */}
            <div className="bg-gray-900 px-4 py-2.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                <span className="text-[11px] font-semibold text-white truncate">비주얼 에디터</span>
              </div>
              <div className="flex items-center gap-0.5 bg-white/10 rounded-lg p-0.5">
                <span className="px-2.5 py-1 rounded-md text-[10px] text-gray-400">A안</span>
                <span className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-blue-600 text-white shadow-sm whitespace-nowrap">B안 편집 중</span>
              </div>
              <span className="text-[10px] font-semibold text-white bg-white/10 px-2.5 py-1 rounded-md flex-shrink-0">저장</span>
            </div>

            <div className="relative p-5 sm:p-6 bg-gray-50/70 min-h-[300px]">
              {/* 페이지 미리보기 (스켈레톤) */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex justify-between items-center mb-5">
                  <div className="w-16 h-3 bg-gray-300 rounded"></div>
                  <div className="flex gap-2">
                    <div className="w-8 h-2.5 bg-gray-100 rounded"></div>
                    <div className="w-8 h-2.5 bg-gray-100 rounded"></div>
                  </div>
                </div>
                <div className="w-3/4 h-5 bg-gray-300 rounded mb-2"></div>
                <div className="w-1/2 h-5 bg-gray-200 rounded mb-6"></div>

                {/* 선택된 요소: CTA 버튼 */}
                <div className="relative inline-block mb-6">
                  <div className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-lg shadow-blue-500/30">
                    지금 시작하기
                  </div>
                  {/* 선택 아웃라인 + 코너 핸들 */}
                  <div className="absolute -inset-1.5 border-2 border-dashed border-blue-500 rounded-lg pointer-events-none">
                    <span className="absolute -top-1 -left-1 w-2 h-2 bg-white border-2 border-blue-500 rounded-[2px]"></span>
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-white border-2 border-blue-500 rounded-[2px]"></span>
                    <span className="absolute -bottom-1 -left-1 w-2 h-2 bg-white border-2 border-blue-500 rounded-[2px]"></span>
                    <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-white border-2 border-blue-500 rounded-[2px]"></span>
                  </div>
                  <MousePointer2 className="absolute -bottom-4 -right-4 w-4 h-4 text-gray-800 fill-white drop-shadow-md" />
                </div>

                <div className="grid grid-cols-3 gap-2.5 opacity-50">
                  {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-100 rounded-lg"></div>)}
                </div>
              </div>

              {/* 플로팅 편집 패널 */}
              <div className="absolute top-16 right-3 sm:right-5 w-40 sm:w-44 bg-white rounded-xl border border-gray-200 shadow-2xl p-3.5">
                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-2.5">버튼 편집</div>
                <div className="mb-3">
                  <div className="text-[9px] text-gray-400 mb-1">텍스트</div>
                  <div className="px-2.5 py-1.5 bg-gray-50 border border-blue-300 ring-1 ring-blue-100 rounded-md text-[11px] text-gray-800 font-medium">
                    지금 시작하기<span className="inline-block w-px h-3 bg-blue-500 ml-0.5 animate-pulse align-middle"></span>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="text-[9px] text-gray-400 mb-1.5">색상</div>
                  <div className="flex gap-1.5">
                    <span className="w-5 h-5 rounded-md bg-blue-600 ring-2 ring-offset-1 ring-blue-400"></span>
                    <span className="w-5 h-5 rounded-md bg-gray-800"></span>
                    <span className="w-5 h-5 rounded-md bg-emerald-500"></span>
                    <span className="w-5 h-5 rounded-md bg-rose-500"></span>
                  </div>
                </div>
                <div className="pt-2.5 border-t border-gray-100 flex items-center justify-between gap-1">
                  <span className="text-[9px] text-gray-400 whitespace-nowrap">변경 1건</span>
                  <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">B안에만 적용</span>
                </div>
              </div>
            </div>
          </div>
        );

      /* ===== 히트맵 (고객 사이트 위 오버레이) ===== */
      case 'heatmap':
        return (
          <div className="bg-slate-800 rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/15 overflow-hidden relative">
            <div className="bg-slate-900 px-4 py-3 flex items-center gap-2 border-b border-slate-700">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
              </div>
              <div className="flex-1 text-center">
                <span className="inline-block px-3 py-0.5 rounded bg-slate-800 text-[10px] text-slate-500 font-mono">switchup.com/demo-shop</span>
              </div>
            </div>
            <div className="relative p-6 min-h-[300px]">
              <div className="opacity-50 grayscale">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-24 h-5 bg-slate-600 rounded"></div>
                  <div className="flex gap-3">
                    <div className="w-10 h-3 bg-slate-700 rounded"></div>
                    <div className="w-10 h-3 bg-slate-700 rounded"></div>
                  </div>
                </div>
                <div className="flex gap-4 mb-6">
                  <div className="w-1/2 space-y-2">
                    <div className="w-3/4 h-6 bg-slate-500 rounded"></div>
                    <div className="w-full h-3 bg-slate-700 rounded"></div>
                    <div className="w-28 h-9 bg-slate-600 rounded mt-3"></div>
                  </div>
                  <div className="w-1/2 h-28 bg-slate-700 rounded-lg"></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-700 rounded"></div>)}
                </div>
              </div>
              <div className="absolute inset-0 mix-blend-screen pointer-events-none">
                <div className="absolute top-[42%] left-[12%] w-40 h-28 bg-gradient-to-r from-red-600 to-orange-500 rounded-full blur-[38px] opacity-80 animate-pulse"></div>
                <div className="absolute top-[30%] right-[18%] w-28 h-28 bg-yellow-500 rounded-full blur-[32px] opacity-60"></div>
                <div className="absolute top-6 right-16 w-32 h-10 bg-blue-600 rounded-full blur-[20px] opacity-50"></div>
              </div>
              <div className="absolute top-[38%] left-[16%] bg-slate-900/90 backdrop-blur border border-slate-600 text-white px-3 py-2 rounded-lg shadow-xl z-10">
                <div className="flex items-center gap-1.5 mb-1">
                  <MousePointer2 className="w-3 h-3 text-orange-400" />
                  <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">CTA 버튼</span>
                </div>
                <div className="text-sm font-bold tracking-tight">2,140 <span className="text-[10px] font-normal text-orange-400">클릭</span></div>
              </div>
            </div>
          </div>
        );

      /* ===== 퍼널 (리포트 카드 + 인사이트 칩) ===== */
      case 'funnel':
        return (
          <div className="relative pb-5">
            <div className="bg-white rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10 p-6 sm:p-7">
              <div className="flex justify-between items-center mb-5">
                <div className="font-semibold text-gray-800 text-sm">구매 전환 퍼널</div>
                <span className="text-[11px] text-gray-400">최근 30일 · 10,000명</span>
              </div>
              <div className="space-y-2.5">
                {[
                  { label: '페이지 방문', value: '10,000', width: '100%', color: 'bg-purple-500', drop: null },
                  { label: '상품 조회', value: '6,200', width: '62%', color: 'bg-purple-500', drop: '-38%' },
                  { label: '장바구니', value: '2,480', width: '24.8%', color: 'bg-violet-500', drop: '-60%' },
                  { label: '결제 시작', value: '1,240', width: '12.4%', color: 'bg-violet-600', drop: '-50%' },
                  { label: '구매 완료', value: '310', width: '3.1%', color: 'bg-pink-500', drop: '-75%' }
                ].map((step, i) => (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[11px] text-gray-500 font-medium">{step.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-gray-800">{step.value}</span>
                        {step.drop && <span className="text-[9px] text-red-500 font-medium">{step.drop}</span>}
                      </div>
                    </div>
                    <div className="h-5 bg-gray-100 rounded-md overflow-hidden">
                      <div className={`h-full ${step.color} rounded-md`} style={{ width: step.width, opacity: 0.85 }}></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-xs text-gray-400">전체 전환율</span>
                <span className="text-lg font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-violet-600">3.1%</span>
              </div>
            </div>

            {/* 플로팅 인사이트 칩 */}
            <div className="absolute bottom-0 left-5 sm:left-7 bg-gray-800 border border-purple-400/40 text-white px-3.5 py-2 rounded-lg shadow-xl">
              <div className="text-[9px] font-semibold text-purple-300 uppercase tracking-wide mb-0.5">개선 포인트</div>
              <div className="text-[11px] font-bold">'구매 완료' 직전에서 75%가 이탈</div>
            </div>
          </div>
        );

      /* ===== 세션 녹화 (재생 플레이어) ===== */
      case 'replay':
        return (
          <div className="bg-[#1A1F2E] rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/15 overflow-hidden relative">
            {/* 플레이어 상단 바 */}
            <div className="bg-[#0F131C] px-4 py-2.5 flex items-center justify-between gap-2 border-b border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">수</span>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-white truncate">차분한 수달 <span className="text-slate-500 font-mono font-normal">· m9x7</span></div>
                  <div className="text-[9px] text-slate-500 truncate">모바일 · Safari · 네이버 검색 유입</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                <span className="text-[9px] font-bold text-red-400 tracking-wider">REC</span>
              </div>
            </div>

            {/* 재생 스테이지 */}
            <div className="p-4 sm:p-5 pb-0">
              <div className="relative h-[210px] sm:h-[230px] bg-white rounded-lg overflow-hidden">
                {/* 쇼핑몰 상품 페이지 재현 */}
                <div className="h-6 bg-white border-b border-gray-200 flex items-center px-3 gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-cyan-500"></div>
                    <span className="text-[7px] font-black text-gray-900 tracking-tight">SWITCH SHOP</span>
                  </div>
                  <div className="hidden sm:flex gap-2 text-[6px] text-gray-500">
                    <span>NEW</span><span className="text-gray-900 font-bold">BEST</span><span>아우터</span><span>상의</span><span>하의</span>
                  </div>
                  <div className="ml-auto relative text-gray-400">
                    <ShoppingCart className="w-2.5 h-2.5" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-cyan-500 text-white text-[5px] font-bold flex items-center justify-center">1</span>
                  </div>
                </div>
                <div className="p-3 grid grid-cols-5 gap-3">
                  {/* 상품 이미지 */}
                  <div className="col-span-2 h-[108px] rounded-lg bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 relative overflow-hidden">
                    <span className="absolute top-1.5 left-1.5 px-1 py-0.5 rounded bg-gray-900 text-white text-[6px] font-bold">BEST</span>
                    <div className="absolute left-5 right-5 top-7 bottom-2 rounded-t-[40%] bg-slate-400/40"></div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-9 w-3 h-8 bg-slate-300/80 rounded-b"></div>
                  </div>
                  {/* 상품 정보 */}
                  <div className="col-span-3">
                    <div className="text-[6px] text-gray-400">상의 · 셔츠</div>
                    <div className="text-[8px] font-bold text-gray-900 leading-tight">코튼 오버핏 셔츠 (3 colors)</div>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="text-[6px] text-gray-400 line-through">52,000원</span>
                      <span className="text-[9px] font-black text-gray-900">39,000원</span>
                      <span className="text-[6px] font-bold text-red-500">25%</span>
                    </div>
                    {/* 색상 옵션 (반복 클릭 대상) */}
                    <div className="mt-1.5 h-[22px] rounded border border-red-300 bg-red-50/60 flex items-center justify-between px-2 fs-shake">
                      <span className="text-[6px] text-gray-600">색상 선택</span>
                      <span className="text-[9px] text-gray-400">▾</span>
                    </div>
                    <div className="mt-1 h-[22px] rounded border border-gray-200 bg-white flex items-center justify-between px-2">
                      <span className="text-[6px] text-gray-400">사이즈 선택</span>
                      <span className="text-[9px] text-gray-300">▾</span>
                    </div>
                    <div className="mt-1.5 flex gap-1">
                      <div className="flex-1 h-[22px] rounded bg-gray-900 text-white text-[6px] font-bold flex items-center justify-center">장바구니 담기</div>
                      <div className="flex-1 h-[22px] rounded bg-cyan-600 text-white text-[6px] font-bold flex items-center justify-center">바로 구매</div>
                    </div>
                  </div>
                </div>
                {/* 추천 상품 */}
                <div className="px-3">
                  <div className="text-[6px] font-bold text-gray-700 mb-1">함께 보면 좋은 상품</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { name: '린넨 와이드 팬츠', price: '45,000' },
                      { name: '베이직 크루넥 티', price: '19,000' },
                      { name: '캔버스 토트백', price: '29,000' },
                    ].map((item, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 overflow-hidden">
                        <div className="h-6 bg-gradient-to-br from-gray-100 to-gray-200"></div>
                        <div className="px-1 py-0.5">
                          <div className="text-[5px] text-gray-600 truncate">{item.name}</div>
                          <div className="text-[6px] font-bold text-gray-900">{item.price}원</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* 커서 + 클릭 리플 */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute fs-cursor">
                    <MousePointer2 className="w-4 h-4 text-gray-900 fill-white drop-shadow" />
                  </div>
                  <span className="absolute fs-ripple w-5 h-5 rounded-full border-2 border-cyan-500"></span>
                  <span className="absolute fs-ripple fs-ripple-2 w-5 h-5 rounded-full border-2 border-cyan-500"></span>
                  <span className="absolute fs-ripple fs-ripple-3 w-5 h-5 rounded-full border-2 border-cyan-500"></span>
                </div>
              </div>
            </div>

            {/* 타임라인 + 컨트롤 */}
            <div className="px-4 sm:px-5 pt-4 pb-4">
              <div className="relative h-1.5 bg-white/10 rounded-full">
                <div className="absolute left-[46%] w-[16%] h-full bg-white/10 rounded-full"></div>
                <div className="absolute left-0 h-full bg-gradient-to-r from-cyan-500 to-sky-500 rounded-full fs-progress"></div>
                {['18%', '26%', '31%', '40%', '78%', '90%'].map((l, i) => (
                  <span key={i} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-300 ring-2 ring-[#1A1F2E]" style={{ left: l }}></span>
                ))}
                <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white shadow ring-2 ring-cyan-500 fs-knob"></span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
                    <Pause className="w-3 h-3 text-gray-900 fill-gray-900" />
                  </span>
                  <span className="text-[11px] font-mono text-slate-300">0:31 <span className="text-slate-600">/ 1:52</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="px-2 py-0.5 rounded bg-white/10 text-[10px] font-bold text-slate-300">2x</span>
                  <span className="px-2 py-0.5 rounded bg-cyan-500/15 text-[10px] font-bold text-cyan-400 inline-flex items-center gap-1 whitespace-nowrap">
                    <SkipForward className="w-3 h-3" /> 비활동 건너뛰기
                  </span>
                </div>
              </div>
            </div>

            {/* 플로팅 인사이트 칩 */}
            <div className="absolute top-[44%] right-3 sm:right-5 bg-gray-900/90 backdrop-blur border border-red-500/30 text-white px-3 py-2 rounded-lg shadow-xl z-10">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-ping"></span>
                <span className="text-[9px] font-semibold text-red-400 uppercase tracking-wide">이탈 직전</span>
              </div>
              <div className="text-[11px] font-bold">옵션 선택 3회 반복</div>
              <div className="text-[9px] text-slate-400">드롭다운이 열리지 않음</div>
            </div>
          </div>
        );

      /* ===== 개인화 (같은 페이지, 방문자별 다른 배너) ===== */
      case 'personalization':
        return (
          <div className="bg-white rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10 overflow-hidden relative">
            <div className="bg-gray-50 border-b border-gray-200/80 px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 text-center">
                <span className="inline-block px-3 py-0.5 rounded bg-white border border-gray-200 text-[10px] text-gray-400 font-mono">switchup.com/demo-shop</span>
              </div>
            </div>

            <div className="p-4 sm:p-5 bg-gray-50/70 grid grid-cols-2 gap-3 sm:gap-4 min-h-[300px]">
              {[
                {
                  who: '회원가입 안 한 고객',
                  eyebrow: 'WELCOME',
                  title: '지금 가입하면 5,000원 쿠폰',
                  cta: '가입하기',
                  banner: 'bg-gradient-to-r from-pink-500 to-rose-500',
                  ctaClass: 'text-pink-600',
                  chip: 'bg-pink-50 text-pink-600 border-pink-200'
                },
                {
                  who: '장바구니 담고 떠난 고객',
                  eyebrow: 'ONLY TODAY',
                  title: '담아두신 셔츠, 오늘까지 10% 할인',
                  cta: '장바구니 보기',
                  banner: 'bg-gradient-to-r from-orange-500 to-amber-500',
                  ctaClass: 'text-orange-600',
                  chip: 'bg-orange-50 text-orange-600 border-orange-200'
                }
              ].map((v) => (
                <div key={v.who} className="flex flex-col">
                  <span className={`self-start inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] sm:text-[10px] font-bold mb-2 ${v.chip}`}>
                    <Users className="w-3 h-3" />
                    {v.who}
                  </span>
                  <div className="flex-1 bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                      <div className="w-12 h-2.5 bg-gray-300 rounded"></div>
                      <div className="flex gap-1.5">
                        <div className="w-5 h-2 bg-gray-100 rounded"></div>
                        <div className="w-5 h-2 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                    {/* 방문자마다 바뀌는 영역 */}
                    <div className={`relative rounded-lg px-3 py-3 overflow-hidden mb-3 ${v.banner}`}>
                      <div className="absolute -right-4 -top-4 w-14 h-14 rounded-full bg-white/15"></div>
                      <div className="relative text-[7px] sm:text-[8px] font-bold tracking-widest text-white/80">{v.eyebrow}</div>
                      <div className="relative text-[10px] sm:text-xs font-bold leading-snug text-white mt-0.5 break-keep">{v.title}</div>
                      <span className={`relative inline-block mt-2 px-2 py-0.5 rounded bg-white text-[8px] sm:text-[9px] font-bold ${v.ctaClass}`}>{v.cta}</span>
                    </div>
                    {/* 공통 영역 */}
                    <div className="grid grid-cols-3 gap-1.5 opacity-60">
                      {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded-md"></div>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-gray-900/90 backdrop-blur border border-pink-500/30 text-white px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Sparkles className="w-3 h-3 text-pink-400" />
                <span className="text-[9px] font-semibold text-pink-400 uppercase tracking-wide">같은 URL</span>
              </div>
              <div className="text-[11px] font-bold">고객마다 다른 내용</div>
            </div>
          </div>
        );

      /* ===== 팝업/배너 (고객 사이트 위 오버레이) ===== */
      case 'popup':
      default:
        return (
          <div className="bg-white rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10 overflow-hidden relative">
            <div className="bg-gray-50 border-b border-gray-200/80 px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
              </div>
              <div className="flex-1 text-center">
                <span className="inline-block px-3 py-0.5 rounded bg-white border border-gray-200 text-[10px] text-gray-400 font-mono">switchup.com/demo-shop</span>
              </div>
            </div>
            <div className="relative min-h-[300px]">
              <div className="p-6 opacity-40 grayscale">
                <div className="w-24 h-5 bg-gray-200 rounded mb-4"></div>
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-16 bg-gray-100 rounded"></div>)}
                </div>
              </div>
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="w-56 bg-white rounded-2xl shadow-2xl overflow-hidden relative">
                  <button className="absolute top-2 right-2 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="h-20 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-4xl">🎁</div>
                  <div className="p-4 text-center">
                    <div className="text-base font-bold text-gray-900 mb-1">첫 구매 10% 할인</div>
                    <div className="text-[11px] text-gray-500 mb-3">지금 가입하고 쿠폰 받기</div>
                    <div className="py-2 bg-emerald-600 rounded-lg text-sm font-bold text-white">쿠폰 받기</div>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 bg-gray-900/90 backdrop-blur border border-emerald-500/30 text-white px-3 py-2 rounded-lg shadow-xl">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Sparkles className="w-3 h-3 text-emerald-400" />
                  <span className="text-[9px] font-semibold text-emerald-400 uppercase tracking-wide">전환</span>
                </div>
                <div className="text-sm font-bold tracking-tight">18.5%</div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <section className="py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* 헤더 */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-900 text-white text-[11px] font-semibold uppercase tracking-[0.12em] mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            <span>All-in-One CRO</span>
          </div>
          <h2 className="text-3xl sm:text-[2.75rem] font-bold text-gray-900 mb-4 leading-[1.15] tracking-tight">
            전환율이 안 오르는 이유는<br className="sm:hidden" /> 여섯 군데 중 하나에 있습니다
          </h2>
          <p className="text-lg text-gray-500 leading-relaxed">
            여섯 가지 기능이 각각 어디를 책임지는지 확인해보세요.
          </p>
        </div>

        {/* ===== 통합 제품 패널 ===== */}
        <div
          className="relative bg-[#0D1220] rounded-3xl border border-white/10 shadow-2xl shadow-gray-900/20 overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          {/* 패널 상단 바: 라벨 + 탭 (제품 네비게이션처럼) */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-4 sm:px-6 py-3.5 border-b border-white/10 bg-white/[0.03]">
            <div className="flex items-center gap-2.5 px-1">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-slate-600"></span>
              </div>
              <span className="text-[11px] font-semibold text-slate-400 tracking-wide font-mono">SWITCHUP — 핵심 기능</span>
            </div>

            <div role="tablist" aria-label="기능 선택" className="grid grid-cols-3 md:flex gap-1 p-1 bg-white/5 rounded-xl">
              {features.map((f) => {
                const isActive = active === f.id;
                return (
                  <button
                    key={f.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleTabClick(f.id)}
                    className={`relative overflow-hidden inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[13px] font-semibold transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1220] ${f.ring} ${isActive ? f.tabActive : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                  >
                    {f.icon}
                    <span>{f.name}</span>

                    {/* 자동 롤링 진행률 바 (수동 전환 후엔 미표시) */}
                    {isActive && autoPlay && !prefersReduced && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20">
                        <span
                          key={active}
                          className={`block h-full bg-white/70 animate-[fsProgress_6s_linear] ${paused ? '[animation-play-state:paused]' : ''}`}
                        ></span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 패널 콘텐츠: 좌 텍스트 / 우 목업 */}
          <div key={active} className="fs-fade grid lg:grid-cols-5">

            {/* 왼쪽: 설명 */}
            <div className="lg:col-span-2 p-6 sm:p-10 lg:p-14 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-white/10">
              {/* 역할 아이라벨 */}
              <div className="flex items-center gap-2.5 mb-5">
                <span className={`h-px w-8 ${activeF.bar}`}></span>
                <span className={`text-xs font-bold uppercase tracking-[0.15em] ${activeF.accent}`}>{activeF.role}</span>
              </div>

              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-5 leading-snug tracking-tight break-keep">
                {activeF.title}
              </h3>
              <p className="text-[15px] lg:text-[17px] text-slate-400 mb-8 leading-[1.8] break-keep">
                {activeF.desc}
              </p>

              <ul className="space-y-3.5 mb-9">
                {activeF.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <Check className={`w-4 h-4 ${activeF.check} flex-shrink-0 stroke-[3]`} />
                    <span className="text-[14px] font-medium">{b}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={activeF.link}
                className={`group inline-flex items-center gap-1.5 text-sm font-bold ${activeF.accent} ${activeF.accentHover} rounded-md outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1220] ${activeF.ring} transition-colors self-start`}
              >
                기능 자세히 보기
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* 오른쪽: 목업 스테이지 */}
            <div className="lg:col-span-3 relative p-6 sm:p-10 lg:p-14 flex items-center justify-center">
              {/* 은은한 격자 배경 */}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:28px_28px] pointer-events-none"></div>
              <div className="relative w-full max-w-xl lg:max-w-2xl">
                {renderMockup(active)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fsFade {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fs-fade { animation: fsFade 0.45s cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes fsProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
        /* 세션 녹화 목업: 커서 궤적 / 클릭 리플 / 진행 바 */
        @keyframes fsCursor {
          0%   { left: 84%; top: 6%; }
          18%  { left: 64%; top: 31%; }
          24%  { left: 65%; top: 33%; }
          30%  { left: 64%; top: 31%; }
          36%  { left: 65%; top: 33%; }
          42%  { left: 64%; top: 31%; }
          60%  { left: 38%; top: 86%; }
          80%  { left: 38%; top: 86%; }
          100% { left: 84%; top: 6%; }
        }
        .fs-cursor { animation: fsCursor 9s ease-in-out infinite; }
        @keyframes fsRipple {
          0%, 17% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
          20%     { opacity: 0.9; transform: translate(-50%, -50%) scale(0.4); }
          30%     { opacity: 0; transform: translate(-50%, -50%) scale(1.8); }
          100%    { opacity: 0; }
        }
        .fs-ripple { left: 64%; top: 31%; animation: fsRipple 9s ease-out infinite; }
        .fs-ripple-2 { animation-delay: 1.1s; }
        .fs-ripple-3 { animation-delay: 2.2s; }
        @keyframes fsShake {
          0%, 16%, 44%, 100% { transform: translateX(0); }
          19%, 31%, 43% { transform: translateX(-1.5px); }
          25%, 37% { transform: translateX(1.5px); }
        }
        .fs-shake { animation: fsShake 9s ease-in-out infinite; }
        @keyframes fsProgressBar { from { width: 0%; } to { width: 100%; } }
        .fs-progress { animation: fsProgressBar 9s linear infinite; }
        @keyframes fsKnob { from { left: 0%; } to { left: 100%; } }
        .fs-knob { animation: fsKnob 9s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .fs-fade, .fs-cursor, .fs-ripple, .fs-shake, .fs-progress, .fs-knob { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default FeatureShowcase;